<template>
  <app-card
    :aria-label="vendor.name"
    interactive
    class="vendor-card"
    :to="{ name: 'Vendor', params: { ...$route.params, vendor: vendor.id } }"
    @click.prevent.capture="selectItem"
  >
    <vendor-image :image-url="vendor.imageUrl" :is-open="isVendorOpen()" :vendor-alias="vendorAlias"></vendor-image>
    <app-link
      :aria-label="buttonText"
      class="vendor-card__button"
      :route-to="{ name: 'Vendor', params: { ...$route.params, vendor: vendor.id } }"
      @click.stop.capture="selectItem"
    >
      {{ buttonText }}
      <i class="vendor-card__button__arrow"></i>
    </app-link>
    <h3 class="vendor-card__title iw-item-name">{{ vendor.name }}</h3>
    <p v-if="vendor.additionalInfo" class="vendor-card__description iw-item-description m-t-10 m-b-10">
      {{ vendor.additionalInfo }}
    </p>
  </app-card>
</template>

<script>
import { mapGetters } from "vuex";
import { showDialog, MODAL } from "@/utils";
import VendorImage from "./vendor-image/VendorImage";
import { IS_VENDOR_OPEN } from "../../../../app-constants";
import AppCard from "@/components/base/app-card/AppCard";
import pickupTimeMixin from "@/mixins/pickup-time.mixin";

export default {
  name: "VendorCard",
  components: { VendorImage, AppCard },
  mixins: [pickupTimeMixin],
  props: {
    vendor: Object
  },
  computed: {
    buttonText() {
      if (this.vendor.pickupTimeSelectionEnabled && this.isVenueOrderAheadAllowed) {
        return "Order";
      }
      if (this.isVendorOpen()) {
        return "Order now";
      }
      return "View Menu";
    },
    ...mapGetters({
      isFullCart: "cart/getCartSize",
      vendorAlias: "venue/getVendorAliasSingular"
    })
  },
  methods: {
    async selectPickupTime(onSelectPickupCompleted) {
      // Only display pickuptime options if the pickupTime selection is Enabled or Venue is OAPI
      if (!this.vendor.pickupTimeSelectionEnabled || !this.isVenueOrderAheadAllowed || !this.isVendorOpen()) {
        onSelectPickupCompleted();
        return;
      }

      //need to fetch the vendor's pickup times before selecting the ASAP one
      await this.fetchVendorPickupTimes({ vendorId: this.vendor.id });
      const pickupTimes = this.getValidTodayPickupTimes(this.vendor.id);

      //will autoselect ASAP as default
      if (this.vendor.pickupTimeAutoSelectEnabled && pickupTimes.length) {
        this.setSelectedPickup({ time: pickupTimes[0], id: this.vendor.id });
        this.setActiveDayPart();
        onSelectPickupCompleted();
        return;
      }

      await this.showPickupTimeOptions(
        this.vendor.id,
        {
          vendorId: this.vendor.id,
          goToNextStep: () => {
            onSelectPickupCompleted();
          }
        },
        true
      );
    },
    isVendorOpen() {
      return !!this.vendor[IS_VENDOR_OPEN];
    },
    clearCart(onClearCartCompleted) {
      // Only clear the cart if the cart has items and the user is switching the active vendor
      if (!this.isFullCart || this.vendor.id === this.activeVendor.id) {
        onClearCartCompleted();
        return;
      }

      showDialog(this, "Navigating to this page will clear your cart", "Select OK to proceed. Select CLOSE to cancel.", [
        {
          title: "Ok",
          handler: () => {
            this.$modal.hide(MODAL.dialogName);
            onClearCartCompleted();
          }
        }
      ]);
    },
    goToVendorPage() {
      this.$router.push(
        {
          name: "Vendor",
          params: { ...this.$route.params, vendor: this.vendor.id }
        },
        () => {}
      );
    },
    selectItem() {
      this.clearCart(() => {
        this.selectPickupTime(() => {
          this.goToVendorPage();
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
@mixin respond-flex($width_value) {
  box-sizing: border-box;
  width: $width_value;
}

.vendor-card {
  display: flex;
  flex-direction: column;
  width: 301px;
  height: auto;

  &__category {
    word-wrap: break-word;
    text-transform: uppercase;
    font-size: 12px;
    color: $text-color;
  }

  &__title {
    word-wrap: break-word;
    margin: 15px 0 9px 8px;
  }

  &__description {
    margin: 3px 8px 8px 8px;
  }

  .link {
    // Override .link class properties (only in vendor-card) to avoid a lot of !important
    width: auto;
    min-width: 93px;
    height: 25px;
    padding: 5px 11px 5px 13px;
    border-radius: 4px;
    border: none;
    font-size: 12px;
    font-weight: 500;
    background-color: $group-3-color-1;
    color: $group-3-color-2;
    align-items: center;
    appearance: auto;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    letter-spacing: normal;
    line-height: normal;
    margin-right: 0;
    text-align: center;
    &:hover {
      text-decoration: none;
    }
  }

  &__button {
    position: relative;
    margin-top: -36px;
    margin-bottom: 8px;
    margin-left: auto;
    right: 8px;

    &__arrow {
      border: solid $group-3-color-4;
      border-width: 0 2px 2px 0;
      display: inline-block;
      padding: 3px;
      margin-left: 2px;
      transform: rotate(-45deg);
      -webkit-transform: rotate(-45deg);
    }
  }

  @include respond-to-tablet {
    @include respond-flex(100%);
  }

  @include respond-to-mobile {
    @include respond-flex(360px);
  }

  @include respond-to-mobile-xs {
    @include respond-flex(100%);
  }
}
</style>
